import { useQueryClient, type QueryClient } from "@tanstack/react-query";

export const UNRESOLVED_Q_KEY = ["/api/app/messages/unresolved"] as const;
// The conversation-list (left-hand inbox) "N open" badge reads each row's
// `unresolvedCount`, which the server computes as an UNCAPPED per-member
// aggregate in getInboxList — NOT from the dashboard queue UNRESOLVED_Q_KEY
// (deliberately capped at the 8 oldest open groups). Keep the inbox badge
// sourced from this key so a host with more than 8 open conversations never
// sees a silent undercount (Task #4160; mirrors the sidebar fix in #4151).
// Every resolve/unresolve path invalidates this key so the badge stays live.
export const INBOX_Q_KEY = ["/api/app/inbox"] as const;
// Task #4325 — server-side unresolved inbox query key. When the "Unresolved
// messages only" toggle is on, the client fetches from this endpoint variant
// instead of the regular paginated inbox. Invalidated alongside INBOX_Q_KEY
// so a resolve action immediately removes the row from the unresolved list.
export const INBOX_UNRESOLVED_Q_KEY = ["/api/app/inbox?unresolvedOnly=1"] as const;
// Task #4340 — lightweight count-only inbox query key. The "Unresolved messages
// only" toggle shows this count whether or not the filter is active, so it must
// be invalidated on every resolve/unresolve just like INBOX_UNRESOLVED_Q_KEY.
// Note: TanStack matches keys by structural equality of segments, so this full
// string key is NOT covered by an INBOX_Q_KEY (["/api/app/inbox"]) invalidation
// — it has to be invalidated explicitly.
export const INBOX_UNRESOLVED_COUNT_Q_KEY = ["/api/app/inbox?countOnly=1"] as const;
export const UNREAD_COUNT_Q_KEY = ["/api/app/messages/unread-count"] as const;

/**
 * The messages-page timeline query key is
 * `["/api/app/members", memberId, "timeline", "infinite"]`. Invalidating the
 * `["/api/app/members", memberId, "timeline"]` prefix matches it (TanStack does
 * prefix matching by default) so the per-bubble resolved badge refreshes too.
 */
export function memberTimelineQueryKey(memberId: number) {
  return ["/api/app/members", memberId, "timeline"] as const;
}

/**
 * The member sidebar's "N open" badge reads the dedicated uncapped per-member
 * count endpoint `GET /api/app/members/:memberId/messages/unresolved-count`
 * (NOT the capped-at-8 `/api/app/messages/unresolved` queue), so it is correct
 * even when a host has more than 8 open conversations. The key segments join to
 * that URL via the default fetcher; invalidating it on every resolve keeps the
 * badge in real-time lockstep with the other surfaces.
 */
export function memberUnresolvedCountQueryKey(memberId: number) {
  return ["/api/app/members", memberId, "messages", "unresolved-count"] as const;
}

/**
 * Invalidates every query that reflects a member's resolve state so the
 * dashboard "Unresolved Messages" card, the messages-page inbox + timeline, and
 * the sidebar "Messages" badge (open/unresolved count) all stay in lockstep no
 * matter which surface fired the mutation. Resolution is account-level at the
 * DB layer (member_chats.resolved_at), so a resolve on either surface must
 * refresh both. Note: mark-read paths must NOT invalidate UNREAD_COUNT_Q_KEY
 * because the badge now counts unresolved messages, not unread ones.
 */
export function invalidateResolveQueries(queryClient: QueryClient, memberId: number) {
  queryClient.invalidateQueries({ queryKey: UNRESOLVED_Q_KEY });
  queryClient.invalidateQueries({ queryKey: INBOX_Q_KEY });
  queryClient.invalidateQueries({ queryKey: INBOX_UNRESOLVED_Q_KEY });
  queryClient.invalidateQueries({ queryKey: INBOX_UNRESOLVED_COUNT_Q_KEY });
  queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_Q_KEY });
  queryClient.invalidateQueries({ queryKey: memberTimelineQueryKey(memberId) });
  queryClient.invalidateQueries({ queryKey: memberUnresolvedCountQueryKey(memberId) });
}

/**
 * Centralises the query-cache invalidations that must fire after every
 * resolve-all or unresolve-all action so all four mutations (thread-header
 * resolve, thread-header undo, sidebar resolve, sidebar undo) stay in lockstep.
 * All three delegate to {@link invalidateResolveQueries} so the unresolved
 * queue, member timeline, inbox badge, and sidebar unread badge move together.
 */
export function useResolveInvalidations(memberId: number) {
  const queryClient = useQueryClient();
  const invalidate = () => invalidateResolveQueries(queryClient, memberId);

  return {
    invalidateAfterResolveAll: invalidate,
    invalidateAfterUnresolveAll: invalidate,
    invalidateAfterSingleResolveToggle: invalidate,
  };
}

// ─── Optimistic conversation-list "N open" badge updates (Task #4166) ─────────
// The left-hand conversation list badge (`badge-unresolved-{memberId}`) reads
// each inbox row's `unresolvedCount` from the ["/api/app/inbox"] cache. Before
// this, that badge only moved once the resolve/unresolve invalidation refetch
// settled, so it briefly lagged the already-optimistic sidebar/dashboard
// counts. These helpers patch the matching inbox row's open-count fields in
// place so the badge updates with no visible delay; the invalidation refetch
// still runs afterwards as the source of truth.

/** The subset of an inbox row the conversation-list "N open" badge depends on. */
type InboxUnresolvedRow = {
  memberId: number;
  unresolvedCount: number;
  oldestUnresolvedAt?: string | null;
};

/** What an optimistic updater returns for the matched row's open-count fields. */
type InboxUnresolvedPatch = Pick<InboxUnresolvedRow, "unresolvedCount" | "oldestUnresolvedAt">;

/** A snapshot of one inbox cache entry, keyed so it can be restored exactly. */
type InboxCacheSnapshot = Array<[readonly unknown[], InboxUnresolvedRow[] | undefined]>;

/**
 * Cancels in-flight inbox fetches, then rewrites the matching member's
 * open-count fields in EVERY cache entry under the ["/api/app/inbox"] prefix
 * through `updater`. Returns the prior snapshots so the caller can roll them
 * back on error. A no-op (returns `{ prevInbox: undefined }`) when no inbox
 * entry has been fetched yet.
 *
 * Uses the FILTER-based getQueriesData/setQueriesData, not getQueryData: the
 * inbox is cached under parameterised keys (`{limit}` for the paginated browse
 * list, `{propertyIds}` for the property-scoped list), and the exact-match
 * getQueryData(["/api/app/inbox"]) reads a slot no query writes — which made
 * this whole optimistic path silently dead.
 */
export async function applyOptimisticInboxResolve(
  queryClient: QueryClient,
  memberId: number,
  updater: (row: InboxUnresolvedRow) => InboxUnresolvedPatch,
): Promise<{ prevInbox: InboxCacheSnapshot | undefined }> {
  await queryClient.cancelQueries({ queryKey: INBOX_Q_KEY });
  const entries = queryClient.getQueriesData<InboxUnresolvedRow[]>({ queryKey: INBOX_Q_KEY });
  const snapshot: InboxCacheSnapshot = entries
    .filter(([, data]) => Array.isArray(data))
    .map(([key, data]) => [key, data]);
  if (snapshot.length === 0) return { prevInbox: undefined };
  queryClient.setQueriesData<InboxUnresolvedRow[]>({ queryKey: INBOX_Q_KEY }, (old) =>
    Array.isArray(old)
      ? old.map((r) => (r.memberId === memberId ? { ...r, ...updater(r) } : r))
      : old,
  );
  return { prevInbox: snapshot };
}

/** Restores a snapshot captured by {@link applyOptimisticInboxResolve}. */
export function rollbackOptimisticInboxResolve(
  queryClient: QueryClient,
  prevInbox: InboxCacheSnapshot | undefined,
) {
  if (!prevInbox) return;
  for (const [key, data] of prevInbox) queryClient.setQueryData(key, data);
}

/**
 * Updater for a "resolve all" / post-reply bulk resolve: the member has no open
 * messages left, so the count goes to 0 and the oldest-open age hint clears.
 */
export function resolveAllInboxRowUpdate(): InboxUnresolvedPatch {
  return { unresolvedCount: 0, oldestUnresolvedAt: null };
}

/**
 * Updater for a single per-bubble resolve: drop the open count by one,
 * clamping at 0 and clearing the oldest-open age hint once nothing is open.
 */
export function decrementInboxRowUpdate(row: InboxUnresolvedRow): InboxUnresolvedPatch {
  const next = Math.max(0, row.unresolvedCount - 1);
  return { unresolvedCount: next, oldestUnresolvedAt: next === 0 ? null : row.oldestUnresolvedAt ?? null };
}

/**
 * Updater factory for re-opening messages (single per-bubble unresolve, or a
 * bulk-resolve "Undo"). `count` is how many messages were re-opened (1 for a
 * single bubble; ids.length for a bulk undo). `openedAt`, when known (e.g. the
 * bubble's createdAt), restores/extends the oldest-open hint to the earliest
 * open timestamp; otherwise the hint is left for the refetch to backfill.
 */
export function incrementInboxRowUpdate(count: number, openedAt?: string | null) {
  return (row: InboxUnresolvedRow): InboxUnresolvedPatch => {
    let oldest = row.oldestUnresolvedAt ?? null;
    if (openedAt) {
      oldest =
        oldest && new Date(oldest).getTime() <= new Date(openedAt).getTime() ? oldest : openedAt;
    }
    return { unresolvedCount: row.unresolvedCount + count, oldestUnresolvedAt: oldest };
  };
}
